<script lang="ts">
  // Screen-space cinematic overlays for the duel (animation-plan §3.2/§3.3): the extra-deck summon
  // CUT-IN (diagonal art banner sweep), the phase banner sweep, and the lethal-blow red vignette.
  // Mounted once by Duel.svelte over BOTH renderers; reads the store + motion bus directly.
  //
  // Division of labour: summon cut-ins are 3D-ONLY (gated on arenaActive() — the 2D board's contract
  // is intent, not cinema); the lethal vignette + cut-in and the phase sweep read in both renderers.
  // Everything here is DOM + CSS animation, so the spectacle costs the arena zero draw calls.
  import { onMount } from "svelte";
  import { onMotion, arenaActive, nameOf, duel, type DuelMotion } from "../../stores/duel.svelte.ts";
  import { settings } from "../../stores/settings.svelte.ts";
  import { cardImageLarge } from "../../../data.ts";
  import { phaseName } from "@ygoevo/protocol";

  interface CutIn { id: number; art: string | null; name: string; label: string; color: string }

  let cutIns = $state<CutIn[]>([]);
  let phaseSweep = $state<{ id: number; text: string; mine: boolean } | null>(null);
  let lethal = $state(false);
  let seq = 0;

  // Reduced motion = the OS setting OR the in-app toggle, checked per event so a mid-duel toggle
  // applies immediately. (matchMedia is feature-checked for the jsdom test environment.)
  const reduced = (): boolean =>
    settings.reducedEffects ||
    (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  /** Cut-in dressing per extra-deck summon type — the §3.3 ceremony palette, mirrored from the arena
   *  portals so the screen-space banner and the in-scene portal read as ONE event. */
  const TYPE_META: Record<string, { label: string; color: string }> = {
    fusion: { label: "Fusion Summon", color: "#b96bff" },
    synchro: { label: "Synchro Summon", color: "#eaf8ff" },
    xyz: { label: "Xyz Summon", color: "#cfd4e6" },
    link: { label: "Link Summon", color: "#19e0ff" },
    ritual: { label: "Ritual Summon", color: "#5a79ff" },
  };

  function showCutIn(c: Omit<CutIn, "id">): void {
    const id = ++seq;
    cutIns = [...cutIns, { ...c, id }];
    setTimeout(() => { cutIns = cutIns.filter((x) => x.id !== id); }, 1500);
  }

  onMount(() => onMotion((m: DuelMotion) => {
    if (reduced()) return;
    if (m.kind === "place" && m.extraType && m.code && arenaActive()) {
      const meta = TYPE_META[m.extraType]!;
      showCutIn({ art: cardImageLarge(m.code), name: nameOf(m.code), label: meta.label, color: meta.color });
    } else if (m.kind === "lethal") {
      lethal = true; // the red edge closes in and HOLDS — released when the end screen owns the moment
      showCutIn({ art: null, name: "", label: "Finishing Blow", color: "#ff2d7e" });
    }
  }));

  // the end screen (z-60) takes over the instant the duel ends — drop the vignette under it
  $effect(() => { if (duel.ended) lethal = false; });

  // ── phase banner sweep ── a new phase sweeps a slim banner across the top: cyan on your turn,
  // magenta on theirs. The very FIRST board is the duel starting, not a phase change — no sweep.
  let prevPhase: number | undefined;
  let seenBoard = false;
  $effect(() => {
    const ph = duel.board?.phase;
    if (ph === undefined) { seenBoard = false; prevPhase = undefined; return; }
    if (!seenBoard) { seenBoard = true; prevPhase = ph; return; }
    if (ph === prevPhase) return;
    prevPhase = ph;
    if (reduced()) return;
    const id = ++seq;
    phaseSweep = { id, text: prettyPhase(ph), mine: duel.board!.turnPlayer === duel.seat };
    setTimeout(() => { if (phaseSweep?.id === id) phaseSweep = null; }, 950);
  });

  /** "MAIN1" → "Main Phase 1", "BATTLE_START" → "Battle Phase" — the player-facing phase names. */
  function prettyPhase(ph: number): string {
    const n = phaseName(ph).replace("PHASE_", "");
    if (n === "MAIN1") return "Main Phase 1";
    if (n === "MAIN2") return "Main Phase 2";
    if (n.startsWith("BATTLE")) return "Battle Phase";
    if (n.startsWith("DAMAGE")) return "Damage Step";
    return `${n.charAt(0)}${n.slice(1).toLowerCase().replace(/_/g, " ")} Phase`;
  }
</script>

<!-- ══ cut-ins: a skewed dark band sweeps one way across the upper third while the card art + name
     slide the other — the MD signature moment (~1.5s in/hold/out) ══ -->
{#each cutIns as c (c.id)}
  <div class="ci pointer-events-none fixed inset-x-0 z-[46] overflow-hidden" style="--ci:{c.color}">
    <div class="ci-band absolute inset-0"></div>
    {#if c.art}
      <img class="ci-art absolute" src={c.art} alt={c.name} draggable="false" />
    {/if}
    <div class="ci-text absolute inset-y-0 flex flex-col justify-center gap-1 {c.art ? 'items-start' : 'ci-center items-center'}">
      <span class="font-display text-[clamp(0.7rem,1.3vw,0.95rem)] font-bold uppercase tracking-[0.34em]" style="color:var(--ci);text-shadow:0 0 16px var(--ci)">{c.label}</span>
      {#if c.name}
        <span class="font-display text-[clamp(1.4rem,3.2vw,2.5rem)] font-bold uppercase leading-tight tracking-[0.05em] text-white" style="text-shadow:0 2px 10px rgba(0,0,0,0.85), 0 0 28px color-mix(in oklab, var(--ci) 55%, transparent)">{c.name}</span>
      {/if}
    </div>
  </div>
{/each}

<!-- ══ lethal vignette: the red edge closes in on the finishing blow and holds until the end screen ══ -->
{#if lethal && !duel.ended}
  <div class="lethal-vig pointer-events-none fixed inset-0 z-[45]"></div>
{/if}

<!-- ══ phase banner: a slim sweep across the top on every phase change ══ -->
{#if phaseSweep}
  {#key phaseSweep.id}
    <div class="pointer-events-none fixed inset-x-0 top-[8%] z-[44] flex justify-center">
      <div class="ph-sweep relative px-10 py-1.5" style="--ph:{phaseSweep.mine ? 'var(--color-cyan)' : 'var(--color-magenta)'}">
        <span class="font-display text-[clamp(0.9rem,1.9vw,1.3rem)] font-bold uppercase tracking-[0.42em]" style="color:var(--ph);text-shadow:0 0 18px var(--ph)">{phaseSweep.text}</span>
      </div>
    </div>
  {/key}
{/if}

<style>
  /* ── cut-in ─────────────────────────────────────────────────────────────────── */
  .ci {
    top: 13%;
    height: clamp(120px, 24vh, 220px);
  }
  /* the skewed dark band: sweeps in from the left, holds, slides off right */
  .ci-band {
    background: linear-gradient(100deg, transparent 2%, rgba(5, 8, 16, 0.94) 13%, rgba(5, 8, 16, 0.94) 87%, transparent 98%);
    border-top: 1px solid color-mix(in oklab, var(--ci) 65%, transparent);
    border-bottom: 1px solid color-mix(in oklab, var(--ci) 65%, transparent);
    box-shadow: 0 0 44px color-mix(in oklab, var(--ci) 20%, transparent);
    animation: ci-band 1.5s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes ci-band {
    0% { transform: skewY(-2.5deg) translateX(-104%); }
    16% { transform: skewY(-2.5deg) translateX(0); opacity: 1; }
    82% { transform: skewY(-2.5deg) translateX(0); opacity: 1; }
    100% { transform: skewY(-2.5deg) translateX(6%); opacity: 0; }
  }
  /* the card art: slides AGAINST the band's sweep (band ←, art →) — the classic cut-in tension */
  .ci-art {
    right: 13%;
    top: 50%;
    height: 160%;
    clip-path: polygon(10% 0, 100% 0, 90% 100%, 0 100%); /* diagonal window, matches the band's slant */
    filter: drop-shadow(0 0 26px color-mix(in oklab, var(--ci) 45%, transparent));
    animation: ci-art 1.5s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes ci-art {
    0% { transform: translate(16vw, -50%) rotate(3deg); opacity: 0; }
    18% { transform: translate(0, -50%) rotate(3deg); opacity: 1; }
    80% { transform: translate(-1.6vw, -50%) rotate(3deg); opacity: 1; }
    100% { transform: translate(-8vw, -50%) rotate(3deg); opacity: 0; }
  }
  .ci-text { left: 11%; animation: ci-text 1.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
  .ci-text.ci-center { left: 0; right: 0; }
  @keyframes ci-text {
    0% { transform: translateX(-9vw); opacity: 0; }
    17% { transform: translateX(0); opacity: 1; }
    80% { transform: translateX(1.4vw); opacity: 1; }
    100% { transform: translateX(7vw); opacity: 0; }
  }

  /* ── lethal vignette ── magenta-red (the house danger accent), edges only: the board stays legible */
  .lethal-vig {
    background: radial-gradient(
      ellipse at center,
      transparent 52%,
      color-mix(in oklab, #ff2d5e 26%, transparent) 84%,
      color-mix(in oklab, #c1002f 52%, transparent) 100%
    );
    animation: lv-in 0.2s ease-out both;
  }
  @keyframes lv-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* ── phase sweep ── */
  .ph-sweep { animation: ph-in 0.9s cubic-bezier(0.16, 1, 0.3, 1) both; }
  /* thin rule lines running out from the label to both edges */
  .ph-sweep::before,
  .ph-sweep::after {
    content: "";
    position: absolute;
    top: 50%;
    height: 1px;
    width: 30vw;
  }
  .ph-sweep::before { right: 100%; background: linear-gradient(to left, var(--ph), transparent); }
  .ph-sweep::after { left: 100%; background: linear-gradient(to right, var(--ph), transparent); }
  @keyframes ph-in {
    0% { transform: translateX(-7vw); opacity: 0; }
    22% { transform: translateX(0); opacity: 1; }
    72% { transform: translateX(1vw); opacity: 1; }
    100% { transform: translateX(6vw); opacity: 0; }
  }
</style>
